/*
This component is a reusable button that can toggle between a normal button and a dropdown button.

To use this button, place it inside a <Grid item></Grid> and define the height and width of the Grid. 
The component will take the height and width of the Grid.

Parameters:
type : "primary", //optional, default value is "primary", can change into secondary
variant : "outlined", //optional, default value is "contained", can change into "outlined" or "text", where "text" only show text
shape : "rectangle", //optional, default value is "rectangle", can change into rounded
disabled : true, //opitional, default value is false, cna change into false
disableElevation: //optional, disable the elevation/shadow added by default to a mui button

@{clickFunc} (optional) - action when the main button is clicked, not need if isSearchOn, isGroupListOn and isLabelButtonOn are false

@{isSearchOn} (boolean) - Whether to show the search bar or not. Default is false (do not show the search bar).
If true, the search bar (currently a TextField with a search icon) is shown.
In the future, this can be changed to an autocomplete or other search functions as needed in the SearchBar file,
with the necessary properties passed in SearchBarProperties.

@{isGroupListOn} (boolean) - Whether to show the list or not. Default is false (do not show the list).
If true, pass GroupProperties to this component.
GroupProperties is an array of objects, each containing a name and function. The name is displayed in the dropdown list,
and the function is the action when the item is clicked. Example:
const GroupProperties = [
  { name: "test1", function: function1 },
  { name: "test2", function: function2 },
  { name: "test3", function: function3 }
]

@{isCheckedGroupList} (boolean) - Subsection of groupList. Whether the group list shows as a button with a checkbox. Defaults to false. Has the ability to have a default checked and disabled item in listItem.isDefaultCheckedAndDisabled 
Each list item has an onClick property that looks at listItem.handleChange

@{isLabelButtonOn} (boolean) - Whether to show the label button (at the bottom of the list). Default is false.
If true, pass LabelButtonProperties to this component.

Example of LabelButtonProperties:
const LabelButtonProperties = { // Required when isLabelButtonOn is true
  type : "primary", //optional, default value is primary, can change between primary and secondary, this property is called 'Accent' on the design
  variant : "outlined", //optional, default value is contained, can change among contained, outlined, and text, this property is called 'Attributes' on the design and corresponds as follows 'Fill'='contained' 'Outline'='outlined' 'No Outline'='text' 
  shape : "rectangle", //optional, default value is rectangle, can change beteen rectangle and rounded
  disabled : true, //optional, default value is false, can change between true and false
  text: "Test", //required, the text in the label button
  iconSize: "20px", //opitional, defualt value is small, can change to other px
  leftIcon: CircleOutlinedIcon, // optional, required if want to show the left icon
  rightIcon: CircleOutlinedIcon, // optional, required if want to show the rigth icon
  function: handleClickTestButton // Required, action when the label button is clicked
}
*/
import React, { useState, useRef, useEffect } from 'react';
import { Grid, Button, List, ListItem, ListItemText, ListItemIcon, Checkbox, ListItemButton } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { theme } from '../../muiTheme';
import SearchBar from '../SearchBar';
import LabelButton from './LabelButton';
import { dropDownButtonStyle, StyledPopover } from './dropdownButtonStyle'

const DropdownButton = ({//we can use GroupProperties as to check if we need to show the group list, but I prefer to keep it seperated with isGroupListOn, this makes it easy to follow and easy to keep the default value as false
  type = "primary",
  variant = "contained",
  shape = "rectangle",
  disabled = false,
  clickFunc,
  isSearchOn = false, 
  SearchBarProperties,
  isGroupListOn = false, 
  GroupProperties,
  isLabelButtonOn = false, 
  LabelButtonProperties,
  children,
  isCheckedGroupList = false,
  dropdownListStyles,
  listItemTextStyles,
  disableElevation = false,
  error = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);
  const hasList = isSearchOn || isGroupListOn || isLabelButtonOn;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    clickFunc && clickFunc()
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    // Reset anchor when switching modes
    setAnchorEl(null);
  }, [isGroupListOn]);

  return (
    <Grid item sx={{ width: '100%', height: '100%' }}>
      <Button
        ref={buttonRef}
        variant= {variant}
        disabled = {disabled}
        endIcon={hasList ? (anchorEl ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />) : null}
        onClick={handleClick}
        disableElevation={disableElevation}
        sx={{
          width: '100%',
          // minWidth: "175px", //commented out for now. Is there a need to have a minimum width?
          height: '100%',
          display: 'flex',
          padding: '5px 20px',
          justifyContent: hasList ? 'space-between' : 'center',
          alignItems: 'center',
          alignSelf: 'stretch',
          border: error ? dropDownButtonStyle[type][variant]["error"].border : dropDownButtonStyle[type][variant]["default"].border,
          borderColor: dropDownButtonStyle[type][variant]["default"].borderColor, //border color controlled by error
          borderRadius: dropDownButtonStyle.borderRadius[shape],
          backgroundColor: dropDownButtonStyle[type][variant]["default"].backgroundColor,
          ...theme.dropdownButton.font,
          color: dropDownButtonStyle[type][variant]["default"].color,
          textTransform: 'none',
          '&:hover': {
            ...dropDownButtonStyle[type][variant].hover
          },
          '&:disabled': {
            ...dropDownButtonStyle[type][variant].disabled, 
          }
        }}
      >
        {children}
      </Button>
      {hasList && <StyledPopover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: {
            width: buttonRef.current ? buttonRef.current.offsetWidth : 'auto',
            ...dropdownListStyles
          },
        }}
      >
        <List sx={{ width: '100%', padding: "0px 0px" }}>
          {isSearchOn && (
            <ListItem 
              key = {"searchbar"}
              sx = {{width: "100%", height: "21px", padding: "0px 10px"}}
            >
              <Grid sx = {{width: "100%", height: "100%"}}>
                <SearchBar SearchBarProperties = {SearchBarProperties}/>
              </Grid>
            </ListItem>
          )}
          {isGroupListOn && GroupProperties?.length > 0 && (
            GroupProperties.map((listItem) => {
              return (
                isCheckedGroupList ? (
                  <ListItem
                    key={listItem.id || listItem.name}
                    sx={{
                      width: "100%",
                      padding: "0px 10px",
                      margin: "5px 0px",
                      cursor: "pointer",
                      '&:hover': {
                        backgroundColor: listItem.isDefaultCheckedAndDisabled ? null : 'var(--Tertiary-100, #EBF8FF)',
                      },
                    }}
                  >
                    <ListItemButton
                      onClick={listItem.handleChange} 
                      disableGutters={true}
                      disabled={listItem.isDefaultCheckedAndDisabled}
                      sx={{ height: "27px" }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          disabled={listItem.isDefaultCheckedAndDisabled}
                          checked={listItem.checked || listItem.isDefaultCheckedAndDisabled}
                          tabIndex={-1}
                          disableRipple
                          color='link'
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={listItem.name}
                        sx={{
                          "& .MuiTypography-root": {
                            ...theme.dropdownButton.font,
                            color: "var(--Primary-500, #16124C)",
                            ...listItemTextStyles
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )
            :
                <ListItem 
                  key = {listItem.id || listItem.name}
                  onClick={()=>{
                    setAnchorEl(null)
                    listItem.function()}}
                  sx = {{
                    width: "100%", 
                    height: listItemTextStyles?.height || "17px", 
                    padding: listItemTextStyles?.padding || "0px 10px",
                    margin: "5px 0px",
                    cursor: "pointer",
                    '&:hover': {
                        backgroundColor: 'var(--Tertiary-100, #EBF8FF)', 
                    },
                  }}
                >
                  <ListItemText 
                    primary={listItem.name} 
                    sx = {{
                      "& .MuiTypography-root" : {
                        ...theme.dropdownButton.font,
                        color: "var(--Primary-500, #16124C)",
                        ...listItem.style 
                      }
                    }}
                  />
                </ListItem>
              )
            })
          )}
          {isLabelButtonOn && (
            <ListItem 
              key= {"labelButton"}
              sx = {{
                width: "100%", 
                height: "30px",
                padding: "0px 10px"
              }}
            >
              <LabelButton 
                // {
                  type =  {LabelButtonProperties.type}
                  variant = {LabelButtonProperties.variant}
                  shape = {LabelButtonProperties.shape}
                  disabled = {LabelButtonProperties.disabled}
                  text = {LabelButtonProperties.text}
                  iconSize = {LabelButtonProperties.iconSize}
                  leftIcon = {LabelButtonProperties.leftIcon}
                  rightIcon = {LabelButtonProperties.rightIcon}
                  function = {LabelButtonProperties.function}
                // }
                // LabelButtonProperties = {LabelButtonProperties} 
              />
            </ListItem>
          )}
        </List>
      </StyledPopover>}
    </Grid>
  );
};

export default DropdownButton;